import "./Attendance.scss";

import { observer } from "mobx-react";
import React from "react";
import { toast } from "react-toastify";
import { academyDomain, attendanceDomain } from "src/domain";
import { modalStore, SettingModal, WarningModal } from "src/modal";
import { util } from "src/utils";
import { soundPreference } from "src/utils/soundPreference";

import { ReactComponent as DeleteIcon } from "/public/images/svgs/icon_delete.svg";
import { ReactComponent as ListIcon } from "/public/images/svgs/icon_list.svg";
import { ReactComponent as RefreshIcon } from "/public/images/svgs/icon_refresh.svg";
import { ReactComponent as SettingIcon } from "/public/images/svgs/icon_setting.svg";

// 제출 성공/실패 후 이 시간(ms) 동안 재제출을 막아, 가드 해제와
// 버튼 비활성화 사이의 틈으로 들어오는 버퍼링된 연타를 방어한다.
const SUBMIT_COOLDOWN_MS = 800;

interface Props {
  // 세로모드에서 출석 내역 드로어를 연다. 닫기는 딤 레이어가 담당한다.
  onOpenList: () => void;
}

const Attendance = ({ onOpenList }: Props) => {
  const onSettingClick = () => {
    modalStore.openModal(<SettingModal />);
  };

  // 재생할 효과음 URL (null이면 재생 안 함)
  const [soundSrc, setSoundSrc] = React.useState<string | null>(null);
  const [koTime, setKoTime] = React.useState(util.getTodayKo());
  const [attendanceCodeList, setAttendanceCode] = React.useState("");
  const isSubmittingRef = React.useRef(false);
  const soundResetTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setKoTime(util.getTodayKo());
    }, 1000);

    return () => {
      clearInterval(timer);
      if (soundResetTimerRef.current !== null) {
        clearTimeout(soundResetTimerRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (attendanceCodeList.length !== 4) return;
    const target = attendanceDomain.getAttendanceTarget(attendanceCodeList);
    if (!target) {
      modalStore.openModal(
        <WarningModal
          text="출석코드를 다시 확인해주세요."
          onConfirm={() => {
            modalStore.closeModal();
            setAttendanceCode("");
          }}
        />
      );
    }
  }, [attendanceCodeList]);

  const onNumberClick = (code: number) => {
    if (attendanceCodeList.length === 4) return;

    const codeStr = attendanceCodeList.concat(`${code}`);

    setAttendanceCode(codeStr);
  };

  const onDeleteClick = () => {
    if (attendanceCodeList.length === 0) return;
    const codeStr = attendanceCodeList.substring(
      0,
      attendanceCodeList.length - 1
    );
    setAttendanceCode(codeStr);
  };

  const onAttendanceClick = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      const attendanceStatus = await attendanceDomain.fetchStudentAttendance(
        attendanceCodeList
      );
      if (!attendanceStatus) return;

      const text =
        attendanceStatus === "ENTER"
          ? "출석 처리되었습니다."
          : "퇴실 처리되었습니다.";
      setSoundSrc(soundPreference.getSoundSrc(attendanceStatus));
      toast(text);
      if (soundResetTimerRef.current !== null) {
        clearTimeout(soundResetTimerRef.current);
      }
      soundResetTimerRef.current = window.setTimeout(() => {
        setSoundSrc(null);
        soundResetTimerRef.current = null;
      }, 2500);
      setAttendanceCode("");
    } catch (error) {
      toast("출석 코드를 다시 확인해주세요.");
      setAttendanceCode("");
    } finally {
      // 즉시 해제하지 않고 쿨다운 후 해제해, 코드 초기화 re-render가
      // 반영되기 전 들어오는 두 번째 터치를 차단한다.
      setTimeout(() => {
        isSubmittingRef.current = false;
      }, SUBMIT_COOLDOWN_MS);
    }
  };

  return (
    <section
      className={`attendance__input--wrap ${
        attendanceDomain.isViewList === "HIDDEN" ? "full" : ""
      }`}
    >
      <div className="attendance__header">
        <button className="header__config" onClick={() => onSettingClick()}>
          <SettingIcon />
          설정
          {soundSrc && <audio autoPlay src={soundSrc}></audio>}
        </button>
        <time className="header__time">{koTime}</time>
        <button
          className="header__refresh-button"
          onTouchEnd={() => window.location.reload()}
        >
          <div className="refresh__circle">
            <RefreshIcon />
          </div>
        </button>
        {/* 내역이 숨김 설정이면 열 대상이 없으므로 버튼도 내보내지 않는다. */}
        {attendanceDomain.isViewList === "VIEW" && (
          <button
            className="header__list-button"
            data-testid="open-student-list"
            aria-label="출석 내역"
            onClick={() => onOpenList()}
          >
            <div className="list__circle">
              <ListIcon />
            </div>
          </button>
        )}
      </div>
      <div className="attendance__body">
        <img
          className="academy__logo"
          src={academyDomain.academyLogo}
          alt="학원 로고 이미지"
        />
        <div className="inserted-numeric__list">
          <div className="inserted-numeric__item">
            {attendanceCodeList[0] || ""}
          </div>
          <div className="inserted-numeric__item">
            {attendanceCodeList[1] || ""}
          </div>
          <div className="inserted-numeric__item">
            {attendanceCodeList[2] || ""}
          </div>
          <div className="inserted-numeric__item">
            {attendanceCodeList[3] || ""}
          </div>
        </div>
        <div className="numeric__button-list">
          {new Array(9).fill(null).map((_, i) => {
            return (
              <button
                key={i}
                className="numeric__item"
                data-testid={`numeric-${i + 1}`}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  onNumberClick(i + 1);
                }}
              >
                {i + 1}
              </button>
            );
          })}
          <div className="numeric__item"></div>
          <button
            className="numeric__item"
            onTouchEnd={(e) => {
              e.stopPropagation();
              onNumberClick(0);
            }}
          >
            0
          </button>
          <button
            className="numeric__item back__button"
            onTouchEnd={() => onDeleteClick()}
          >
            <DeleteIcon />
          </button>
        </div>
      </div>
      <button
        className="attendance__submit-button"
        disabled={attendanceCodeList.length !== 4}
        onTouchEnd={() => onAttendanceClick()}
      >
        {attendanceDomain.getAttendanceText(attendanceCodeList)}
      </button>
    </section>
  );
};

export default observer(Attendance);
